import FileViewer from "components/file-components/fileViewer2";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import MainLayout from "layouts/MainLayout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getAssetAppealById } from "services/assetAppealService";
import { AssetAppeal, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import "../AssetRecovery/AssetRecoveryRegistration/AssetReferralRegisterCss.css";
import AssetAppealReportContainer from "./AppealReport/AssetAppealReportContainer";
import AssetAppealDetailsContainer from "./ProsecutorAppeal/AssetAppeal/AssetAppealDetailsContainer";

const AssetAppealContainer: React.FC = () => {
  const { t } = useTranslation();
  const tabs = [t("appeal"), t("report")];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const { id } = useParams<{ id: string }>();
  const [appeal, setAppeal] = React.useState<AssetAppeal | null>(null);
  const [loading, setLoading] = useState(true);
  const { loginData } = useAuth();
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const handleTabSelect = (tab: string) => {
    setActiveTab(tab);
  };
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const { showTooltip } = useTooltip();
  useEffect(() => {
    setLoading(true);
    getAssetAppealById(id as string)
      .then((data) => {
        setAppeal(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(t("error_fetching_appeal_details"), error);
        setLoading(false);
        showTooltip(errorHandler(error));
      });
  }, [id]);

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case t("appeal"):
        return (
          <AssetAppealDetailsContainer
            appeal={appeal as AssetAppeal}
            isAssetCoordinator={isAssetCoordinator}
            isAssetProsecutor={isAssetProsecutor}
            loading={loading}
            setActiveTab={setActiveTab}
            setAppeal={setAppeal}
            fileRowProps={fileRowProps}
          />
        );
      case t("report"):
        return (
          <AssetAppealReportContainer
            appealId={id as string}
            appeal={appeal as AssetAppeal}
            setActiveTab={setActiveTab}
            setAppeal={setAppeal}
          />
        );
      default:
        return (
          <div className="p-6 bg-white rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4">
              {t("appeal_details")}
            </h3>
            <p>{t("appeal_info_displayed_here")}</p>
          </div>
        );
    }
  };

  return (
    <MainLayout>
      <div className="incidentRegistration">
        <div className="">
          <div className="text-2xl font-semibold text-gray-700 flex">
            <svg
              width="28"
              height="29"
              viewBox="0 0 28 29"
              fill="none"
              xmlns="http://www.w3.org/2000/svg "
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.33398 14.815C5.31904 15.9763 8.46917 16.6575 11.6673 16.8334V19.1666H16.334V16.8334C19.5244 16.6335 22.6687 15.9693 25.6673 14.8616V23.8334H2.33398V14.815ZM17.5006 4L18.6673 5.16665V7.5H25.6673V13.5784C22.0901 14.914 18.3088 15.6205 14.4906 15.6666H13.6506C9.78127 15.6248 5.9499 14.898 2.33398 13.52V7.5H9.33398V5.16665L10.5006 4H17.5006ZM16.334 6.33335H11.6673V7.5H16.334V6.33335Z"
                fill="#696969"
              />
            </svg>
            <p className="referral-main-title ml-3">
              {t("asset_appeal_details")}
            </p>
          </div>
        </div>

        <div className="flex space-x-4 mt-2">
          {tabs.map((tab) => {
            const isActive = activeTab === tab;

            return (
              <button
                key={tab}
                onClick={() => handleTabSelect(tab)}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  isActive
                    ? "referral-active-tab"
                    : "referral-inactive-tab hover:bg-gray-100"
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {renderTabContent()}
      </div>
      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </MainLayout>
  );
};

export default AssetAppealContainer;
